import type { CosmosData } from "../parser/types.ts";

// NÚCLEO COSMOS — HUD "IA viva" estilo JARVIS: reator holográfico animado que serve de
// menu-legenda (diagrama de funcionalidades = subsistemas) + painel de alertas do Council.

type Level = "ok" | "warn" | "critical";

const CAPS = [
  { label: "Estrutura", desc: "6 sondas · heartbeat horário" },
  { label: "Sinais vitais", desc: "health · GA4 · GAM · Meta" },
  { label: "Autonomia", desc: "Stop hook + reconciliador" },
  { label: "Domínios externos", desc: "WordPress · Meta · GCloud" },
  { label: "Histórico vivo", desc: "changelog + sparklines" },
];

interface Alert { level: Level; msg: string; }

export class Jarvis {
  private panel!: HTMLElement;
  private bodyEl!: HTMLElement;
  private open = false;
  private timer = 0;

  constructor(private data: CosmosData) {}

  mount(root: HTMLElement) {
    root.insertAdjacentHTML("beforeend", `
      <button id="jv-btn" class="panel" title="Núcleo Cosmos — I.A. viva"><span class="jv-btn-core"></span></button>
      <div id="jarvis" class="panel">
        <div class="jv-scan"></div>
        <div class="jv-head">
          <div class="jv-title">NÚCLEO&nbsp;COSMOS<span class="jv-cursor">▮</span></div>
          <button id="jv-close" title="fechar">×</button>
        </div>
        <div id="jv-body" class="jv-body"><div class="jv-boot">inicializando núcleo…</div></div>
      </div>`);
    this.panel = root.querySelector("#jarvis") as HTMLElement;
    this.bodyEl = root.querySelector("#jv-body") as HTMLElement;
    (root.querySelector("#jv-btn") as HTMLElement).addEventListener("click", () => this.toggle());
    (root.querySelector("#jv-close") as HTMLElement).addEventListener("click", () => this.toggle(false));
  }

  toggle(force?: boolean) {
    this.open = force ?? !this.open;
    this.panel.classList.toggle("open", this.open);
    window.clearInterval(this.timer);
    if (this.open) { this.refresh(); this.timer = window.setInterval(() => this.refresh(), 15000); }
  }

  private reactor(level: Level): string {
    const segs = 24;
    const lines = Array.from({ length: segs }, (_, i) => {
      const a = (i / segs) * Math.PI * 2, r1 = 46, r2 = 52;
      return `<line x1="${(60 + r1 * Math.cos(a)).toFixed(1)}" y1="${(60 + r1 * Math.sin(a)).toFixed(1)}" x2="${(60 + r2 * Math.cos(a)).toFixed(1)}" y2="${(60 + r2 * Math.sin(a)).toFixed(1)}"/>`;
    }).join("");
    return `<svg class="jv-reactor jv-${level}" viewBox="0 0 120 120" aria-hidden="true">
      <g class="jv-seg">${lines}</g>
      <circle class="jv-ring jv-r1" cx="60" cy="60" r="40"/>
      <circle class="jv-ring jv-r2" cx="60" cy="60" r="30"/>
      <circle class="jv-ring jv-r3" cx="60" cy="60" r="21"/>
      <circle class="jv-core-glow" cx="60" cy="60" r="15"/>
      <circle class="jv-core" cx="60" cy="60" r="11"/>
    </svg>`;
  }

  private async refresh() {
    try {
      const [st, al] = await Promise.all([
        fetch(`/api/sync/status?t=${Date.now()}`, { cache: "no-store" }).then((r) => r.json()),
        fetch(`/api/council/alerts?t=${Date.now()}`, { cache: "no-store" }).then((r) => r.json()),
      ]);
      this.render(st, al);
    } catch { if (!this.bodyEl.dataset.ok) this.bodyEl.innerHTML = `<div class="jv-boot">núcleo offline</div>`; }
  }

  private render(st: Record<string, any>, al: Record<string, any>) {
    const t = st.totals || {};
    const gals: Array<{ gal: string; n: number }> = st.ecosystem_by_gal || [];
    const scopes = new Set((st.runs || []).map((r: { scope: string }) => r.scope)).size || 6;
    const counts = al.counts || {};
    const level: Level = counts.critical ? "critical" : counts.warn ? "warn" : "ok";
    const alerts: Alert[] = al.alerts || [];

    const galDots = gals.map((g) => `<span class="jv-gal"><i style="background:${this.galColor(g.gal)}"></i>${esc(g.gal)}<b>${g.n}</b></span>`).join("");
    const caps = CAPS.map((c, i) => `<div class="jv-cap" style="--i:${i}"><span class="jv-cap-dot"></span><div class="jv-cap-txt"><div class="jv-cap-l">${esc(c.label)}</div><div class="jv-cap-d">${esc(c.desc)}</div></div><span class="jv-cap-on">ATIVO</span></div>`).join("");
    const alertHtml = alerts.length
      ? alerts.map((a) => `<div class="jv-alert jv-${a.level}"><span class="jv-al-ic"></span><span>${esc(a.msg)}</span></div>`).join("")
      : `<div class="jv-alert jv-ok"><span class="jv-al-ic"></span><span>todos os sistemas nominais</span></div>`;
    const statusTxt = level === "ok" ? "COSMOS ONLINE" : level === "warn" ? "ATENÇÃO" : "CRÍTICO";
    const badge = (k: string, cls: string) => (counts[k] ? `<em class="jv-badge ${cls}">${counts[k]}</em>` : "");

    this.bodyEl.dataset.ok = "1";
    this.bodyEl.innerHTML = `
      <div class="jv-core-wrap">${this.reactor(level)}
        <div class="jv-status jv-${level}"><span class="jv-pulse"></span>${statusTxt}</div>
      </div>
      <div class="jv-readout">
        <div><span>NÓS</span><b>${(t.vault || 0) + (t.active || 0)}</b></div>
        <div><span>ECOSSISTEMA</span><b>${t.active || 0}</b></div>
        <div><span>ESCOPOS</span><b>${scopes}</b></div>
        <div><span>SINAIS VITAIS</span><b>${t.vitals || 0}</b></div>
      </div>
      <div class="jv-sec-t">SUBSISTEMAS</div>
      <div class="jv-caps">${caps}</div>
      <div class="jv-sec-t">GALÁXIAS DO ECOSSISTEMA</div>
      <div class="jv-gals">${galDots}</div>
      <div class="jv-sec-t">ALERTAS DO CONSELHO ${badge("critical", "crit")}${badge("warn", "warn")}</div>
      <div class="jv-alerts">${alertHtml}</div>`;
  }

  private galColor(g: string): string {
    return (this.data.gal as Record<string, { color: string }>)[g]?.color ?? "#38bdf8";
  }
}

function esc(s: string): string {
  return String(s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] as string));
}
